import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthLayout from '../../components/layout/AuthLayout';
import { Button } from '../../components/ui';
import { sendCustomVerificationEmail } from '../../lib/api';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    user,
    isEmailVerified,
    isPhoneVerified,
    refreshUser,
    signOut,
    authLoading,
  } = useAuth();

  const [resendCooldown, setResendCooldown] = useState(0);
  const [status, setStatus] = useState('idle'); // idle, sending, sent, error
  const [errorMessage, setErrorMessage] = useState('');
  const [checking, setChecking] = useState(false);
  const hasCheckedInitial = useRef(false);
  const hasHandledVerifiedParam = useRef(false);

  // Handle ?verified=true query param (user clicked email verification link)
  useEffect(() => {
    if (hasHandledVerifiedParam.current) return;

    const params = new URLSearchParams(location.search);
    if (params.get('verified') === 'true') {
      hasHandledVerifiedParam.current = true;
      // User just verified via email link - refresh auth state
      refreshUser();
    }
  }, [location.search, refreshUser]);

  // Redirect if already verified
  useEffect(() => {
    if (isEmailVerified) {
      // Go to phone verification if not verified, otherwise home
      if (!isPhoneVerified) {
        navigate('/auth/verify-phone', { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  }, [isEmailVerified, isPhoneVerified, navigate]);

  // Set initial cooldown on mount (assume email was just sent from signup)
  useEffect(() => {
    if (!hasCheckedInitial.current) {
      hasCheckedInitial.current = true;
      // Check sessionStorage for last send time
      const lastSent = sessionStorage.getItem('verifyEmailSentAt');
      if (lastSent) {
        const elapsed = Math.floor((Date.now() - parseInt(lastSent)) / 1000);
        const remaining = 60 - elapsed;
        if (remaining > 0) {
          setResendCooldown(remaining);
        }
      } else {
        // First time on this page this session - assume email was just sent
        sessionStorage.setItem('verifyEmailSentAt', Date.now().toString());
        setResendCooldown(60);
      }
    }
  }, []);

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResend = async () => {
    if (resendCooldown > 0 || status === 'sending') return;

    setStatus('sending');
    setErrorMessage('');

    try {
      await sendCustomVerificationEmail();
      setStatus('sent');
      setResendCooldown(60);
      sessionStorage.setItem('verifyEmailSentAt', Date.now().toString());
      // Clear success message after 5 seconds
      setTimeout(() => setStatus('idle'), 5000);
    } catch (err) {
      console.error('Failed to resend verification email:', err);
      setStatus('error');
      setErrorMessage(err.message || 'Failed to send email. Please try again.');
    }
  };

  const handleCheckStatus = async () => {
    setChecking(true);
    try {
      await refreshUser();
    } finally {
      setChecking(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth/login');
  };

  if (!user) {
    return null;
  }

  return (
    <AuthLayout title="Verify your email">
      <div className="text-center space-y-6">
        {/* Email icon */}
        <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-primary/20">
          <svg
            className="h-8 w-8 text-primary"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        {/* Instructions */}
        <div className="space-y-2">
          <p className="text-midnight/70">We sent a verification link to</p>
          <p className="font-semibold text-midnight">{user?.email}</p>
        </div>

        <p className="text-sm text-midnight/50">
          Click the link in your email to verify your account.
          Check your spam folder if you don't see it.
        </p>

        {/* Status messages */}
        {status === 'sent' && (
          <div className="rounded-lg bg-success/20 border border-success/30 p-4">
            <p className="text-sm text-success">Verification email sent! Check your inbox.</p>
          </div>
        )}

        {status === 'error' && (
          <div className="rounded-lg bg-error/20 border border-error/30 p-4">
            <p className="text-sm text-error">{errorMessage}</p>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3 pt-2">
          <Button
            onClick={handleCheckStatus}
            fullWidth
            loading={checking || authLoading}
          >
            I've verified my email
          </Button>

          <Button
            variant="outline-dark"
            onClick={handleResend}
            fullWidth
            disabled={resendCooldown > 0 || status === 'sending'}
            loading={status === 'sending'}
          >
            {resendCooldown > 0
              ? `Resend email (${resendCooldown}s)`
              : 'Resend verification email'}
          </Button>
        </div>

        {/* Sign out / use different email */}
        <div className="pt-4 border-t border-midnight/10">
          <button
            onClick={handleSignOut}
            className="text-sm text-midnight/50 hover:text-midnight transition-colors"
          >
            Sign out and use a different email
          </button>
        </div>
      </div>
    </AuthLayout>
  );
}
